import React from 'react';
import { motion } from 'framer-motion';
import { Users, Building2, Award, TrendingUp } from 'lucide-react';
import { useCountUp } from '../hooks/useCountUp';
import './StatsSection.css';

const StatsSection: React.FC = () => {
  const stats = [
    {
      icon: Users,
      number: 10000,
      suffix: '+',
      label: 'Candidates Placed',
      description: 'Talented professionals placed in leading organizations'
    },
    {
      icon: Building2,
      number: 50,
      suffix: '+',
      label: 'Clients',
      description: 'Trusted by companies across various industries'
    },
    {
      icon: Award,
      number: 1000,
      suffix: '+',
      label: 'Vacancies Filled',
      description: 'Successfully filled positions across diverse sectors'
    },
    {
      icon: TrendingUp,
      number: 10,
      suffix: '+',
      label: 'Years Experience',
      description: 'Established expertise since 2013'
    }
  ];

  const StatCard: React.FC<{ stat: typeof stats[0]; index: number }> = ({ stat, index }) => {
    const { count, elementRef } = useCountUp({ 
      end: stat.number, 
      duration: 2000, 
      delay: index * 200 
    });

    const formatNumber = (num: number) => {
      if (num >= 1000) {
        return num.toLocaleString();
      }
      return num.toString();
    };

    return (
      <motion.div
        className="stat-card"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        viewport={{ once: true }}
        whileHover={{ y: -5 }}
      >
        <div className="stat-icon">
          <stat.icon size={32} />
        </div>
        <div className="stat-content">
          <div className="stat-number" ref={elementRef}>
            {formatNumber(count)}{stat.suffix}
          </div>
          <h3 className="stat-label">{stat.label}</h3>
          <p className="stat-description">{stat.description}</p>
        </div>
      </motion.div>
    );
  };

  return (
    <section className="stats-section section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Our Impact in Numbers</h2>
          <p className="section-subtitle">
            Delivering measurable results and building lasting partnerships
          </p>
        </motion.div>

        <div className="stats-grid">
          {stats.map((stat, index) => (
            <StatCard key={stat.label} stat={stat} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
